// Typeable, filterable combobox for cross-ref id selection. Replaces the
// native <select> in the loot / quest objective / reward editors so the
// admin can type-to-search instead of scrolling a long flat list.
//
// Behavior:
//   - Click the field → opens the panel with all options.
//   - Type → filters case-insensitively against the option label.
//   - Arrow up/down / Enter → keyboard navigation. Esc → close.
//   - Click an option or press Enter → commits + closes.
//   - Blur outside the field → closes without committing typed-but-unselected
//     text (reverts to the current value).
//   - Stale current value (not in options) is preserved in the input + flagged
//     visually (red text) so the admin sees broken cross-refs.

import { el } from "./dom.js";

export interface ComboboxOptions {
  options: readonly string[];
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
}

export function createCombobox(opts: ComboboxOptions): HTMLElement {
  const wrapper = el("div", { class: "combobox" });
  const input = el("input", {
    type: "text",
    class: "combobox-input",
    value: opts.value,
    placeholder: opts.placeholder ?? "Search…",
    autocomplete: "off",
    spellcheck: false,
  }) as HTMLInputElement;
  const panel = el("div", { class: "combobox-panel" });
  panel.style.display = "none";
  wrapper.append(input, panel);

  let allOptions = [...opts.options];
  let currentValue = opts.value;
  let filterText = "";
  let highlightIdx = 0;

  function isStale(): boolean {
    return currentValue !== "" && !allOptions.includes(currentValue);
  }

  function updateStaleClass(): void {
    input.classList.toggle("stale", isStale());
  }
  updateStaleClass();

  function commit(value: string): void {
    currentValue = value;
    input.value = value;
    filterText = "";
    closePanel();
    updateStaleClass();
    opts.onChange(value);
  }

  function openPanel(): void {
    if (panel.style.display === "block") return;
    panel.style.display = "block";
    highlightIdx = 0;
    renderOptions();
  }

  function closePanel(): void {
    panel.style.display = "none";
  }

  function renderOptions(): void {
    panel.replaceChildren();
    const needle = filterText.toLowerCase();
    const visible = needle
      ? allOptions.filter((o) => o.toLowerCase().includes(needle))
      : allOptions;
    if (visible.length === 0) {
      panel.append(el("div", { class: "combobox-empty" }, "No matches"));
      return;
    }
    // Clamp highlight inside the new filtered range.
    if (highlightIdx >= visible.length) highlightIdx = visible.length - 1;
    if (highlightIdx < 0) highlightIdx = 0;
    for (let i = 0; i < visible.length; i++) {
      const value = visible[i]!;
      const opt = el(
        "div",
        {
          class: "combobox-option" + (i === highlightIdx ? " highlighted" : ""),
          onmousedown: (e: Event) => {
            // mousedown (not click) so the input's blur doesn't fire first
            // and close the panel before the value commits.
            e.preventDefault();
            commit(value);
          },
        },
        value,
      );
      panel.append(opt);
    }
  }

  input.addEventListener("focus", openPanel);
  input.addEventListener("click", openPanel);
  input.addEventListener("blur", () => {
    // Delay closing so a click on a panel option can commit first via
    // mousedown. Reset input to currentValue (drop any uncommitted typing).
    window.setTimeout(() => {
      input.value = currentValue;
      filterText = "";
      closePanel();
    }, 120);
  });
  input.addEventListener("input", () => {
    filterText = input.value;
    highlightIdx = 0;
    openPanel();
    renderOptions();
  });
  input.addEventListener("keydown", (e) => {
    const visible = filterText
      ? allOptions.filter((o) => o.toLowerCase().includes(filterText.toLowerCase()))
      : allOptions;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      openPanel();
      highlightIdx = Math.min(visible.length - 1, highlightIdx + 1);
      renderOptions();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      openPanel();
      highlightIdx = Math.max(0, highlightIdx - 1);
      renderOptions();
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (visible.length > 0) commit(visible[highlightIdx]!);
    } else if (e.key === "Escape") {
      e.preventDefault();
      input.value = currentValue;
      filterText = "";
      closePanel();
      input.blur();
    }
  });

  return wrapper;
}
